<template>
  <b-card no-body class="birthdays-shell-card">
    <b-card-header class="birthdays-page-header">
      <div class="d-flex align-items-center birthdays-title-block">
        <div class="birthday-icon-wrap">
          <feather-icon icon="GiftIcon" size="20" class="text-white" />
        </div>
        <div class="birthdays-heading-text">
          <h4 class="mb-0 birthdays-title">Birthdays</h4>
          <small class="birthdays-subtitle">Today · 7 days · 30 days</small>
        </div>
      </div>
      <div class="birthdays-filter-row">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="birthdays-filter-chip"
          :class="{ 'birthdays-filter-chip--active': activeFilter === tab.key }"
          @click="activeFilter = tab.key"
        >
          {{ tab.label }}
          <span class="birthdays-filter-chip__count">{{
            counts[tab.key]
          }}</span>
        </button>
      </div>
    </b-card-header>

    <div class="birthdays-columns-wrap">
      <b-row class="birthdays-columns-row">
        <b-col
          v-for="block in sectionBlocks"
          :key="block.key"
          cols="12"
          md="4"
          class="birthdays-col mb-1 mb-md-0"
        >
          <div class="birthday-column-card">
            <div class="column-card-header">
              <div class="column-card-header__main">
                <div class="column-icon-wrap">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="text-primary"
                  />
                </div>
                <div class="column-title-wrap min-w-0">
                  <span class="column-title">{{ block.title }}</span>
                  <span class="column-count-pill">{{ filterList(block.rawList).length }}</span>
                </div>
              </div>
              <span
                v-if="block.hint"
                class="column-hint"
                >{{ block.hint }}</span
              >
            </div>

            <div class="column-card-body">
              <div
                v-if="block.loading"
                class="d-flex justify-content-center align-items-center column-inner-fill"
              >
                <b-spinner small variant="primary" type="grow" />
              </div>

              <div
                v-else-if="filterList(block.rawList).length === 0"
                class="birthdays-empty-state column-inner-fill"
              >
                <feather-icon
                  icon="CalendarIcon"
                  size="40"
                  class="birthdays-empty-state__icon mb-1"
                />
                <p class="birthdays-empty-state__msg mb-0">{{ block.emptyMessage }}</p>
              </div>

              <div v-else class="column-scroll-list">
                <div
                  v-for="(item, idx) in filterList(block.rawList)"
                  :key="block.key + '-' + idx"
                  class="birthday-row"
                  :class="{ 'birthday-row--today': item.daysUntil === 'today' }"
                >
                  <div class="birthday-row__inner">
                    <div class="birthday-avatar birthday-avatar--ring">
                      <img
                        v-if="item.picture"
                        :src="getPicturePreview(item.picture)"
                        width="40"
                        height="40"
                        style="border-radius: 50%; object-fit: cover"
                        referrerpolicy="no-referrer"
                        alt=""
                        @error="onPicturePreviewError($event, item.picture)"
                      />
                      <div
                        v-else
                        class="avatar-placeholder-sm d-flex align-items-center justify-content-center rounded-circle"
                        :class="avatarBg(item.type)"
                      >
                        <feather-icon
                          icon="UserIcon"
                          size="16"
                          class="text-white"
                        />
                      </div>
                    </div>

                    <div class="birthday-row__mid">
                      <div class="birthday-name-row">
                        <span class="birthday-name">{{
                          item.name
                        }}{{ item.surname ? " " + item.surname : "" }}</span>
                        <span
                          class="birthdays-type-pill"
                          :class="'birthdays-type-pill--' + (item.type || 'student')"
                        >
                          {{ item.type }}
                        </span>
                      </div>
                      <div class="birthday-meta text-truncate">
                        <span v-if="item.grNo">GR {{ item.grNo }}</span>
                        <span v-if="item.meta_class">
                          <span v-if="item.grNo" class="mx-50 text-muted">·</span
                          >{{ item.meta_class }}</span
                        >
                        <span v-if="item.designation">
                          <span class="mx-50 text-muted">·</span
                          >{{ item.designation }}</span
                        >
                      </div>
                    </div>

                    <div class="birthday-row__aside">
                      <span
                        class="birthdays-when-pill"
                        :class="relativePillClass(item.daysUntil)"
                      >
                        <feather-icon
                          v-if="item.daysUntil === 'today'"
                          icon="StarIcon"
                          size="12"
                          class="mr-25"
                        />
                        {{
                          item.daysUntil === "today"
                            ? "Today 🎂"
                            : item.daysUntil
                        }}
                      </span>
                      <div
                        v-if="item.date_of_birth"
                        class="birthday-dob-line"
                      >
                        <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                        {{ formatDob(item.date_of_birth) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BRow,
  BCol,
  BSpinner,
  BImg,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { getMediaPreviewUrl, handleDrivePreviewError } from "@/utils/gdriveUpload";

export default {
  name: "Birthdays",
  components: { BCard, BCardHeader, BRow, BCol, BSpinner, BImg },
  props: {
    campusID: { type: Number, default: null },
    /** Happiness foundation only — other domains show students & staff birthdays only */
    showDonors: { type: Boolean, default: false },
  },
  data() {
    return {
      activeFilter: "all",
      todayList: [],
      rangeList: [],
      monthList: [],
      loadingToday: false,
      loadingRange: false,
      loadingMonth: false,
    };
  },
  computed: {
    tabs() {
      const base = [
        { key: "all", label: "All" },
        { key: "student", label: "Students" },
        { key: "staff", label: "Staff" },
      ];
      if (this.showDonors) {
        base.push({ key: "donor", label: "Donors" });
      }
      return base;
    },
    /** Dedupe across the three sections so header counts are not inflated. */
    dedupedUnion() {
      const merged = this.dedupeBirthdays([
        ...this.todayList,
        ...this.rangeList,
        ...this.monthList,
      ]);
      if (this.showDonors) return merged;
      return merged.filter((i) => i.type !== "donor");
    },
    counts() {
      const base = this.dedupedUnion;
      return {
        all: base.length,
        student: base.filter((i) => i.type === "student").length,
        staff: base.filter((i) => i.type === "staff").length,
        donor: base.filter((i) => i.type === "donor").length,
      };
    },
    sectionBlocks() {
      const todayIso = this.formatIsoDate(new Date());
      const from = new Date();
      const toSeven = new Date(from);
      toSeven.setDate(toSeven.getDate() + 6);
      const toThirty = new Date(from);
      toThirty.setDate(toThirty.getDate() + 29);
      return [
        {
          key: "today",
          title: "Today",
          hint: todayIso,
          rawList: this.todayList,
          loading: this.loadingToday,
          emptyMessage: "No birthdays today.",
        },
        {
          key: "range",
          title: "Next 7 days",
          hint: `${this.formatIsoDate(from)} → ${this.formatIsoDate(toSeven)}`,
          rawList: this.rangeList,
          loading: this.loadingRange,
          emptyMessage: "No birthdays in the next 7 days.",
        },
        {
          key: "month",
          title: "Next 30 days",
          hint: `${this.formatIsoDate(from)} → ${this.formatIsoDate(toThirty)}`,
          rawList: this.monthList,
          loading: this.loadingMonth,
          emptyMessage: "No birthdays in the next 30 days.",
        },
      ];
    },
  },
  watch: {
    campusID() {
      this.loadAllBirthdays();
    },
    showDonors(show) {
      if (!show && this.activeFilter === "donor") {
        this.activeFilter = "all";
      }
    },
  },
  created() {
    this.loadAllBirthdays();
  },
  methods: {
    ...mapActions({ get: "get" }),

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    onPicturePreviewError(event, url) {
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },

    filterList(list) {
      if (!list || !list.length) return [];
      let filtered = list;
      if (!this.showDonors) {
        filtered = filtered.filter((item) => item.type !== "donor");
      }
      if (this.activeFilter === "all") return filtered;
      return filtered.filter((item) => item.type === this.activeFilter);
    },

    dedupeBirthdays(items) {
      const seen = new Set();
      const out = [];
      for (const item of items) {
        const key = [
          item.type || "",
          item.id != null ? item.id : "",
          item.grNo != null ? item.grNo : "",
          item.date_of_birth || "",
          item.name || "",
          item.surname || "",
        ].join("|");
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(item);
      }
      return out;
    },

    startOfDay(d) {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    },

    formatIsoDate(d) {
      const x = new Date(d);
      const y = x.getFullYear();
      const m = String(x.getMonth() + 1).padStart(2, "0");
      const day = String(x.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    mergeBirthdayResponse(res) {
      if (!res) return [];
      if (Array.isArray(res)) {
        if (this.showDonors) return res;
        return res.filter((item) => item.type !== "donor");
      }
      const students = res.students || [];
      const staff = res.staff || [];
      const donors = this.showDonors ? res.donors || [] : [];
      return [...students, ...staff, ...donors];
    },

    nextBirthdayOnOrAfter(dob, refDate) {
      const refDay = this.startOfDay(refDate);
      const d = new Date(dob);
      if (isNaN(d.getTime())) return refDay;
      const m = d.getMonth();
      const day = d.getDate();
      let y = refDay.getFullYear();
      let cand = new Date(y, m, day);
      if (cand < refDay) {
        cand = new Date(y + 1, m, day);
      }
      return cand;
    },

    labelDaysUntil(nextBirthday, today) {
      const diff = Math.round(
        (this.startOfDay(nextBirthday) - this.startOfDay(today)) /
          86400000
      );
      if (diff <= 0) return "today";
      if (diff === 1) return "Tomorrow";
      return `In ${diff} days`;
    },

    /**
     * For "this month" list: compare MD to today in the current calendar year.
     * Upcoming dates → "In N days"; already passed → "N days ago" / "Yesterday".
     */
    labelRelativeToCalendarMonthCelebration(celebrationDate, today) {
      const t0 = this.startOfDay(today).getTime();
      const c0 = this.startOfDay(celebrationDate).getTime();
      const diffDays = Math.round((c0 - t0) / 86400000);
      if (diffDays === 0) return "today";
      if (diffDays === 1) return "Tomorrow";
      if (diffDays > 1) return `In ${diffDays} days`;
      const ago = Math.abs(diffDays);
      if (ago === 1) return "Yesterday";
      return `${ago} days ago`;
    },

    celebrationThisCalendarYear(dob, refDate) {
      const d = new Date(dob);
      if (isNaN(d.getTime())) return null;
      const y = refDate.getFullYear();
      const celeb = new Date(y, d.getMonth(), d.getDate());
      return celeb;
    },

    applyBirthdayLabels(items, period) {
      const today = new Date();
      const enriched = items.map((item) => {
        let daysUntil = "—";
        let sortTime = 0;
        /** Only used when period === 'month': 0 today, 1 upcoming, 2 past */
        let sortGroup = 1;

        if (period === "today") {
          daysUntil = "today";
          sortTime = this.startOfDay(today).getTime();
          sortGroup = 0;
        } else if (period === "month" && item.date_of_birth) {
          const celeb = this.celebrationThisCalendarYear(
            item.date_of_birth,
            today
          );
          if (!celeb) {
            sortTime = Number.MAX_SAFE_INTEGER;
            sortGroup = 3;
          } else {
            sortTime = this.startOfDay(celeb).getTime();
            const t0 = this.startOfDay(today).getTime();
            const diffDays = Math.round((sortTime - t0) / 86400000);
            daysUntil = this.labelRelativeToCalendarMonthCelebration(
              celeb,
              today
            );
            if (diffDays === 0) sortGroup = 0;
            else if (diffDays > 0) sortGroup = 1;
            else sortGroup = 2;
          }
        } else if (item.date_of_birth) {
          const next = this.nextBirthdayOnOrAfter(item.date_of_birth, today);
          sortTime = next.getTime();
          daysUntil = this.labelDaysUntil(next, today);
          sortGroup = 1;
        } else {
          sortTime = Number.MAX_SAFE_INTEGER;
          sortGroup = 3;
        }

        return { ...item, daysUntil, _sortGroup: sortGroup, _sortTime: sortTime };
      });

      enriched.sort((a, b) => {
        if (period === "month") {
          if (a._sortGroup !== b._sortGroup) return a._sortGroup - b._sortGroup;
          if (a._sortGroup === 1) return a._sortTime - b._sortTime;
          if (a._sortGroup === 2) return b._sortTime - a._sortTime;
          return 0;
        }
        if (a.daysUntil === "today" && b.daysUntil !== "today") return -1;
        if (b.daysUntil === "today" && a.daysUntil !== "today") return 1;
        return a._sortTime - b._sortTime;
      });

      return enriched.map(({ _sortTime, _sortGroup, ...rest }) => rest);
    },

    campusQuery() {
      const db = this.$store.state.userData.db;
      const cID = this.campusID || this.$store.state.userData.cId;
      return { db, cID };
    },

    async fetchTodayList() {
      this.loadingToday = true;
      try {
        const { db, cID } = this.campusQuery();
        const date = this.formatIsoDate(new Date());
        const url = `${this.$store.state.domain}Users/GetBirthdayListByDate?db=${encodeURIComponent(
          db
        )}&cID=${cID}&date=${date}`;
        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const merged = this.mergeBirthdayResponse(res);
        this.todayList = this.applyBirthdayLabels(merged, "today");
      } catch {
        this.todayList = [];
      } finally {
        this.loadingToday = false;
      }
    },

    async fetchRangeList() {
      this.loadingRange = true;
      try {
        const { db, cID } = this.campusQuery();
        const from = new Date();
        const to = new Date(from);
        to.setDate(to.getDate() + 6);
        const url = `${this.$store.state.domain}Users/GetBirthdayListByRange?db=${encodeURIComponent(
          db
        )}&cID=${cID}&dateFrom=${this.formatIsoDate(
          from
        )}&dateTo=${this.formatIsoDate(to)}`;
        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const merged = this.mergeBirthdayResponse(res);
        this.rangeList = this.applyBirthdayLabels(merged, "range");
      } catch {
        this.rangeList = [];
      } finally {
        this.loadingRange = false;
      }
    },

    async fetchMonthList() {
      this.loadingMonth = true;
      try {
        const { db, cID } = this.campusQuery();
        const from = new Date();
        const to = new Date(from);
        to.setDate(to.getDate() + 29);
        const url = `${this.$store.state.domain}Users/GetBirthdayListByRange?db=${encodeURIComponent(
          db
        )}&cID=${cID}&dateFrom=${this.formatIsoDate(
          from
        )}&dateTo=${this.formatIsoDate(to)}`;
        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const merged = this.mergeBirthdayResponse(res);
        this.monthList = this.applyBirthdayLabels(merged, "range");
      } catch {
        this.monthList = [];
      } finally {
        this.loadingMonth = false;
      }
    },

    loadAllBirthdays() {
      return Promise.all([
        this.fetchTodayList(),
        this.fetchRangeList(),
        this.fetchMonthList(),
      ]);
    },

    formatDob(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    avatarBg(type) {
      if (type === "staff") return "bg-gradient-warning";
      if (type === "donor") return "bg-gradient-danger";
      return "bg-gradient-primary";
    },

    relativePillClass(daysUntil) {
      if (!daysUntil || daysUntil === "—") return "birthdays-when-pill--muted";
      if (daysUntil === "today") return "birthdays-when-pill--today";
      if (
        daysUntil === "Yesterday" ||
        (typeof daysUntil === "string" && daysUntil.endsWith(" ago"))
      ) {
        return "birthdays-when-pill--past";
      }
      return "birthdays-when-pill--soon";
    },
  },
};
</script>

<style lang="scss" scoped>
.min-w-0 {
  min-width: 0;
}

.object-fit-cover {
  object-fit: cover;
}

/* Shell card — aligned with marksEntryStudent student-detail-card */
::v-deep .birthdays-shell-card.card {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid #e0d8ff;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
}

.birthdays-page-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem 1.25rem;
  padding: 1.15rem 1.35rem 1rem !important;
  background: transparent !important;
  border-bottom: 2px solid #f0edff !important;
}

.birthdays-title-block {
  gap: 0.85rem;
}

.birthday-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  box-shadow: 0 6px 18px rgba(124, 93, 250, 0.35);
}

.birthdays-heading-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.birthdays-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.35px;
}

.birthdays-subtitle {
  font-size: 0.82rem;
  font-weight: 600;
  color: #8a86b7;
  letter-spacing: 0.02em;
}

.birthdays-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.birthdays-filter-chip {
  appearance: none;
  border: 2px solid #e0d8ff;
  background: #fff;
  color: #63607d;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0 1rem;
  min-height: 2.375rem;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  line-height: 1.2;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);

  &:hover {
    border-color: #cbb9ff;
    color: #4c3d91;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(124, 93, 250, 0.14);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.25);
  }
}

.birthdays-filter-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.4rem;
  font-size: 0.68rem;
  font-weight: 800;
  border-radius: 9999px;
  background: #f3f0ff;
  color: #7c5dfa;
  border: 1px solid #e0d8ff;
}

.birthdays-filter-chip--active {
  border-color: transparent;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #fff;
  box-shadow: 0 6px 22px rgba(124, 93, 250, 0.42);
  transform: translateY(-1px);

  &:hover {
    border-color: transparent;
    color: #fff;
    box-shadow: 0 8px 26px rgba(124, 93, 250, 0.48);
  }

  .birthdays-filter-chip__count {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.35);
  }
}

.birthdays-columns-wrap {
  padding: 1rem 1.15rem 1.25rem;
}

.birthdays-columns-row {
  margin-left: -0.45rem;
  margin-right: -0.45rem;
}

.birthdays-col {
  padding-left: 0.45rem;
  padding-right: 0.45rem;
}

.birthday-column-card {
  display: flex;
  flex-direction: column;
  height: 340px;
  border-radius: 16px;
  border: 1.5px solid #f0edff;
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  box-shadow: 0 6px 22px rgba(99, 102, 241, 0.1);
  overflow: hidden;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    border-color: #d4c7ff;
    box-shadow: 0 10px 28px rgba(99, 102, 241, 0.14);
  }
}

.column-card-header {
  flex-shrink: 0;
  padding: 0.65rem 0.85rem;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.35rem 0.75rem;
  border-bottom: 2px solid #f0edff;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
}

.column-card-header__main {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.column-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  border: 2px solid #e0d8ff;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.1);
  color: #7c5dfa;
}

.column-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.column-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.2px;
}

.column-count-pill {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 800;
  min-width: 1.65rem;
  height: 1.65rem;
  padding: 0 0.45rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
  color: #0369a1;
  border: 1px solid #bae6fd;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
}

.column-hint {
  font-size: 0.72rem;
  font-weight: 600;
  color: #9ca3af;
  max-width: 100%;
  text-align: right;
}

@media (min-width: 768px) {
  .column-hint {
    max-width: 46%;
  }
}

.column-card-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.column-inner-fill {
  flex: 1;
  min-height: 0;
}

.column-scroll-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.35rem 0.45rem 0.55rem;
  scrollbar-width: thin;
  scrollbar-color: #c4b5fd transparent;
}

.column-scroll-list::-webkit-scrollbar {
  width: 3px;
}

.column-scroll-list::-webkit-scrollbar-track {
  background: transparent;
}

.column-scroll-list::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: linear-gradient(180deg, #cbb9ff 0%, #a78bfa 100%);
  /* Visually thinner bar inside the gutter */
  border: 1px solid transparent;
  background-clip: padding-box;
}

.column-scroll-list::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #b8a3fc 0%, #8b5cf6 100%);
  background-clip: padding-box;
}

.birthdays-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.25rem 1rem;
  margin: 0.5rem;
  border-radius: 14px;
  border: 2px dashed #e5e7eb;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
}

.birthdays-empty-state__icon {
  color: #c4b5fd;
  opacity: 0.85;
}

.birthdays-empty-state__msg {
  font-size: 0.82rem;
  font-weight: 600;
  color: #6b7280;
  max-width: 220px;
  line-height: 1.45;
}

.birthday-row {
  padding: 0.25rem 0;
}

.birthday-row__inner {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.7rem;
  border-radius: 12px;
  border: 1px solid transparent;
  background: transparent;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    background: linear-gradient(90deg, #faf9ff 0%, #ffffff 100%);
    border-color: #ece9fb;
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.07);
  }
}

.birthday-row--today .birthday-row__inner {
  background: linear-gradient(90deg, #ecfdf5 0%, #ffffff 100%);
  border-color: #a7f3d0;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.12);

  &:hover {
    background: linear-gradient(90deg, #d1fae5 0%, #fafffd 100%);
    border-color: #6ee7b7;
  }
}

.birthday-avatar {
  flex-shrink: 0;
}

.birthday-avatar--ring {
  padding: 2px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e0d8ff 0%, #f0edff 100%);
}

.avatar-placeholder-sm {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.birthday-row__mid {
  flex: 1;
  min-width: 0;
}

.birthday-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.45rem;
}

.birthday-name {
  font-size: 0.875rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.15px;
}

.birthdays-type-pill {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: capitalize;
  letter-spacing: 0.06em;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  border: 1px solid;
  line-height: 1.2;
}

.birthdays-type-pill--student {
  color: #5b21b6;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  border-color: #c4b5fd;
  box-shadow: 0 2px 6px rgba(91, 33, 182, 0.12);
}

.birthdays-type-pill--staff {
  color: #b45309;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-color: #fcd34d;
  box-shadow: 0 2px 6px rgba(180, 83, 9, 0.12);
}

.birthdays-type-pill--donor {
  color: #be123c;
  background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  border-color: #fda4af;
  box-shadow: 0 2px 6px rgba(190, 18, 60, 0.12);
}

.birthday-meta {
  margin-top: 0.2rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
}

.birthday-row__aside {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  max-width: 108px;
}

.birthdays-when-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.28rem 0.5rem;
  border-radius: 999px;
  text-align: center;
  line-height: 1.25;
  white-space: normal;
  border: 1px solid transparent;
}

.birthdays-when-pill--muted {
  color: #6b7280;
  background: #f3f4f6;
  border-color: #e5e7eb;
}

.birthdays-when-pill--today {
  color: #fff;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border: none;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
}

.birthdays-when-pill--soon {
  color: #5b21b6;
  background: linear-gradient(135deg, #ede9fe 0%, #e9d5ff 100%);
  border-color: #c4b5fd;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.18);
}

.birthdays-when-pill--past {
  color: #4b5563;
  background: #f9fafb;
  border-color: #e5e7eb;
}

.birthday-dob-line {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 600;
  color: #9ca3af;
}
</style>
